import {
  GetOrgData,
  GetOrgDataByPeriod,
  GetOrganization,
} from "./requests/organizations";
import { GetAllSubscriptions } from "./requests/organizationSubscripions";
import { GetPatientReport } from "./requests/patientReport";
// import { GetDoctor } from './requests/doctors';
import i18next from "i18next";
import moment from "moment";
import { GetRequestData } from "./requests/userRequests";

/**
 * Return hours & minutes with a Date
 * @param date
 * @returns
 */
export const getTime = (date: Date) => {
  if (date !== null) {
    const hour =
      date.getHours() >= 10 ? date.getHours() : `0${date.getHours()}`;
    const minutes =
      date.getMinutes() >= 10 ? date.getMinutes() : `0${date.getMinutes()}`;
    return `${hour}:${minutes}`;
  }
  return "";
};

/**
 *
 * @param bytes
 * @param decimals
 * @returns
 */
export function formatBytes(bytes: string, decimals = 2) {
  if (!+parseInt(bytes, 10)) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(parseInt(bytes, 10)) / Math.log(k));

  return `${parseFloat((parseInt(bytes, 10) / Math.pow(k, i)).toFixed(dm))} ${
    sizes[i]
  }`;
}

/**
 * Function to escape specials tags
 * @param text
 * @returns
 */
export const escapeHtml = (text: any) => {
  return text
    ?.replace(/&/g, "&amp;")
    ?.replace(/</g, "&lt;")
    ?.replace(/>/g, "&gt;")
    ?.replace(/"/g, "&quot;")
    ?.replace(/'/g, "&#039;");
};

/**
 * Function to return user initials
 * @param user
 * @returns
 */
export const getUserInitial = (user: any) => {
  return `${user?.username}`;
};

/**
 *
 * @param date
 * @param dueDate
 * @returns
 */
export const dueDateInfo = (date: string | Date, dueDate: string | Date) => {
  if (dueDate === null) {
    return 0;
  }
  return moment.duration(moment(dueDate).diff(moment(date))).days();
};

/**
 *
 * @param start
 * @param dueDate
 * @returns
 */
export const dueDatePercent = (
  start: string | Date,
  dueDate: string | Date
) => {
  const uday = new Date();
  // Calculons le nombre total de jours entre la date de debut et la date d'echeance
  const nbDays = dueDateInfo(start, dueDate);
  // Calculons le nombre total de jours restant entre aujourd'hui et la date d'echeance
  const nbDaysLeft = dueDateInfo(uday, dueDate);
  if (dueDate === null) {
    return 0;
  }
  return nbDaysLeft === 0
    ? 100
    : Math.round(((nbDays - nbDaysLeft) * 100) / nbDays);
};

/**
 *
 * @param item
 * @returns
 */
export const tooltipMessage = (item: any) => {
  if (item?.dueDate === null) {
    return "Aucune échéance pour cette tache.";
  }
  return dueDateInfo(new Date(), item?.dueDate) > 0
    ? `J-${dueDateInfo(new Date(), item?.dueDate)} avant échéance de la tache`
    : dueDateInfo(new Date(), item?.dueDate) === 0
    ? `J-0 dernière journée avant échéance de la tache`
    : `Echéance dépassée de ${Math.abs(
        dueDateInfo(new Date(), item?.dueDate)
      )} jour(s)`;
};

export const formatNumber = (num: number): number | string => {
  if (num >= 0 && num < 10) {
    return `0${num}`;
  }
  return num;
};

/**
 * Convert frequency
 * @param frequency
 * @returns
 */
export const convertFrequency = (frequency: string) => {
  let result = 0;
  switch (frequency) {
    case i18next.t("common:frequencies.list.monthly"):
      result = 1;
      break;
    case i18next.t("common:frequencies.list.every_2_month"):
      result = 2;
      break;
    case i18next.t("common:frequencies.list.every_3_month"):
      result = 3;
      break;
    case i18next.t("common:frequencies.list.every_4_month"):
      result = 4;
      break;
    case i18next.t("common:frequencies.list.every_5_month"):
      result = 5;
      break;
    case i18next.t("common:frequencies.list.every_6_month"):
      result = 6;
      break;
    case i18next.t("common:frequencies.list.every_7_month"):
      result = 7;
      break;
    case i18next.t("common:frequencies.list.every_8_month"):
      result = 8;
      break;
    case i18next.t("common:frequencies.list.every_9_month"):
      result = 9;
      break;
    case i18next.t("common:frequencies.list.every_10_month"):
      result = 10;
      break;
    case i18next.t("common:frequencies.list.every_11_month"):
      result = 11;
      break;
    case i18next.t("common:frequencies.list.every_years"):
      result = 12;
      break;
    case i18next.t("common:frequencies.list.every_2_years"):
      result = 24;
      break;
    case i18next.t("common:frequencies.list.every_3_years"):
      result = 36;
      break;
    case i18next.t("common:frequencies.list.every_4_years"):
      result = 48;
      break;
    case i18next.t("common:frequencies.list.every_5_years"):
      result = 60;
      break;
    case i18next.t("common:frequencies.list.every_6_years"):
      result = 72;
      break;
    case i18next.t("common:frequencies.list.every_7_years"):
      result = 84;
      break;
    case i18next.t("common:frequencies.list.every_8_years"):
      result = 96;
      break;
    case i18next.t("common:frequencies.list.as_soon_as"):
    case i18next.t("common:frequencies.list.once"):
    case i18next.t("common:frequencies.list.at_each_consultation"):
    case i18next.t("common:frequencies.list.to_be_defined_with_doctor"):
    default:
      break;
  }
  return result;
};

/**
 * Find next date
 * @param date
 * @param frequency
 * @returns
 */
export const getNextDate = (date: string, frequency: string) => {
  // convert frequency
  let uday = moment();
  const dateLaPlusRecente = new Date(date);
  // convert frequency
  let monthFrequency = convertFrequency(frequency);
  // Calculer la prochaine date en ajoutant le nombre de mois à la date la plus récente
  let nextDate: any = moment(
    new Date(
      dateLaPlusRecente.setMonth(dateLaPlusRecente.getMonth() + monthFrequency)
    )
  );
  // if nextdate < today then nextdate = uday
  if (nextDate < uday && monthFrequency >= 12) {
    nextDate = moment();
  }
  return nextDate?.format("YYYY-MM-DD");
};

/**
 * Returns user age
 * @param birth_date
 * @returns
 */
export const getAge = (birth_date: Date) => {
  const diff_ms = Date.now() - birth_date.getTime();
  const age_dt = new Date(diff_ms);
  return Math.abs(age_dt.getUTCFullYear() - 1970);
};

/**
 * Find the last exams
 * @param historyList
 * @returns
 */
export const findMoreRecent = (historyList: any) => {
  historyList.sort((a: any, b: any) => {
    // Turn your strings into dates, and then subtract them
    // to get a value that is either negative, positive, or zero.
    let dateA = a.date !== null ? new Date(a.date)?.getTime() : 0;
    let dateB = b.date !== null ? new Date(b.date)?.getTime() : 0;
    let result: any = dateA - dateB;
    return result;
  });
  return historyList;
};

/**
 * Extract an attribute from all patients (in patient_report), parse it into numbers, and filter out NaN values
 * @param allPatients - Array of patient data
 * @param attribute - Attribute to extract from each patient
 * @returns Array of parsed attribute values (numbers)
 */
const extractAttribute = (allPatients: any[], attribute: string): number[] => {
  return allPatients
    .map((patient) => parseInt(patient?.patient_report?.[attribute]))
    .filter((value) => !isNaN(value));
};

/**
 * Calculate the number of patients with id non null
 * @param allPatients - Array of patient data
 * @returns The number of patients
 */
export const getNumberOfPatients = (allPatients: any[]): number => {
  const filteredPatients = allPatients.filter(
    (patient) => patient.id !== null && patient?.user_id !== null
  );
  return filteredPatients?.length;
};

/**
 * Calculate the number of doctors with id non null
 * @param allDoctors - Array of patient data
 * @returns The number of patients
 */
export const getNumberOfdoctors = (allDoctors: any[]): number => {
  const filteredDoctors = allDoctors.filter(
    (doctor) => doctor.id !== null && doctor?.users_id !== null
  );
  return filteredDoctors?.length;
};

/**
 * Calculate the number of patients which completed the survey
 * @param allPatients - Array of patient data
 * @returns The number of patients
 */
export const getNumberOfSurveyCompleted = (allPatients: any[]): number => {
  const filteredPatients = allPatients.filter(
    (patient) => patient?.patient_report?.resultIsReady === true
  );
  return filteredPatients?.length;
};

/**
 * Calculate the average of the parameter "attributeName"
 * @param allPatients - Array of patient data
 * @param attributeName - Name of the attribute
 * @returns The average number of the attribute asked
 */
export const getAverageOf = (
  allPatients: any[],
  attributeName: string
): number => {
  const attributes = extractAttribute(allPatients, attributeName);

  if (attributes?.length === 0) return 0;

  const sum = attributes?.reduce((acc, x) => acc + x, 0);
  return Math.floor(sum / attributes?.length);
};

/**
 * Calculate the total amount of appointments taken
 * @param allPatients - Array of patient data
 * @returns The total amount of appointments taken
 */
export const getTotalRdv = (allPatients: any[]): number => {
  let totalAppointments = 0;
  for (const patient of allPatients) {
    const appointment =
      patient?.patient_report?.rdvInfos?.selectedTime?.startTime;
    if (appointment) {
      totalAppointments += 1;
    }
  }
  return totalAppointments;
};

/**
 * Calculate the total amount of patients who downloaded the PDF summary or the doctor register guide
 * @param allPatients - Array of patient data
 * @returns The total amount of patients
 */
export const getPdfDownloadCounts = (allPatients: any[]): any => {
  let total = {
    hasDownloadedPdf: 0,
    hasDownloadedPdfAndAppointment: 0,
    hasDownloadedDoctorGuide: 0,
  };
  for (const patient of allPatients) {
    if (patient?.patient_report?.rdvInfos?.hasDownloadedPdf === true) {
      total.hasDownloadedPdf += 1;
      if (patient?.patient_report?.appointmentWithDoc === true) {
        total.hasDownloadedPdfAndAppointment += 1;
      }
    }
    if (patient?.patient_report?.rdvInfos?.hasDownloadedDoctorGuide === true) {
      total.hasDownloadedDoctorGuide += 1;
    }
  }
  return total;
};

/**
 * Calculate the total amount of appointments taken
 * @param allPatients - Array of patient data
 * @returns The total amount of appointments taken
 */
export const getTotalRdvBeforeSigning = (allPatients: any[]): number => {
  let totalAppointments = 0;
  for (const patient of allPatients) {
    const createdDate = moment(patient?.createdAt);
    for (const exam of patient?.patient_exams) {
      if (
        exam?.patient_rdvs &&
        Array.isArray(exam.patient_rdvs) &&
        exam.patient_rdvs?.length > 0
      ) {
        for (let i = 0; i < exam.patient_rdvs?.length; i++) {
          const rdv = exam.patient_rdvs[i];
          const rdv_date = rdv.date !== null ? moment(rdv.date) : null;
          if (
            rdv.date !== null &&
            rdv.status === "Pris" &&
            rdv_date?.isBefore(createdDate)
          ) {
            totalAppointments += 1;
            break;
          }
        }
      }
    }
  }
  return totalAppointments;
};

/**
 * Calculate the total amount of appointments taken
 * @param allPatients - Array of patient data
 * @returns The total amount of appointments taken
 */
export const getTotalRdvAfterSigning = (allPatients: any[]): number => {
  let totalAppointments = 0;
  for (const patient of allPatients) {
    const createdDate = moment(patient?.createdAt);
    for (const exam of patient?.patient_exams) {
      if (
        exam?.patient_rdvs &&
        Array.isArray(exam.patient_rdvs) &&
        exam.patient_rdvs?.length > 0
      ) {
        for (let i = 0; i < exam.patient_rdvs?.length; i++) {
          const rdv = exam.patient_rdvs[i];
          const rdv_date = rdv.date !== null ? moment(rdv.date) : null;
          if (
            rdv.date !== null &&
            rdv.status === "Pris" &&
            rdv_date?.isAfter(createdDate)
          ) {
            totalAppointments += 1;
            break;
          }
        }
      }
    }
  }
  return totalAppointments;
};

/**
 * Calculate the average of appointments taken
 * @param allPatients - Array of patient data
 * @returns The average number of appointments taken
 */
export const getAverageRdv = (allPatients: any[]): number => {
  const averageAppointments = Math.floor(
    getTotalRdv(allPatients) / allPatients?.length
  );
  return averageAppointments;
};

type StepType = {
  step: string;
  count: number;
};

/**
 * Calculate the number of patients that reached each step of use in the app
 * @param allPatients - Array of patient data
 * @returns The array with steps and their count
 */

export const getStepCounts = (allPatients: any[]): StepType[] => {
  // filtering the array of patients for each step
  // - user is confirmed
  const confirmedPatients = allPatients.filter((patient) => {
    return patient?.user_id?.confirmed;
    // && !patient?.user_id?.blocked;
  });
  // - patient started the onboarding
  const startedOnboardingPatients = confirmedPatients.filter((patient) => {
    return !patient?.firstConnexion;
  });
  // - patient finished the onboarding
  const finishedOnboardingPatients = startedOnboardingPatients.filter(
    (patient) => {
      return patient?.patient_report?.resultIsReady;
    }
  );
  // - patient has taken a rdv with a doctor
  const rdvTakenPatients = finishedOnboardingPatients.filter((patient) => {
    return (
      patient?.patient_report?.appointmentWithDoc &&
      patient?.patient_report?.rdvInfos?.selectedTime?.startTime !== ""
    );
  });
  // - patient has updated his planning with old date
  const updatedPlanningPatientsWithOldDate = finishedOnboardingPatients.filter(
    (patient) => {
      let examRdvTaken = false;
      const createdDate = moment(patient?.createdAt);
      patient?.patient_exams.forEach((exam: any) => {
        if (
          exam?.patient_rdvs &&
          Array.isArray(exam.patient_rdvs) &&
          exam.patient_rdvs?.length > 0
        ) {
          for (let i = 0; i < exam.patient_rdvs?.length; i++) {
            const rdv = exam.patient_rdvs[i];
            const rdv_date = rdv.date !== null ? moment(rdv.date) : null;
            if (
              rdv.date !== null &&
              rdv.status === "Pris" &&
              rdv_date?.isBefore(createdDate)
            ) {
              examRdvTaken = true;
              break;
            }
          }
        }
      });
      return examRdvTaken;
    }
  );
  // - patient has updated his planning with date ater registration date
  const updatedPlanningPatients = finishedOnboardingPatients.filter(
    (patient) => {
      let examRdvTaken = false;
      const createdDate = moment(patient?.createdAt);
      patient?.patient_exams.forEach((exam: any) => {
        if (
          exam?.patient_rdvs &&
          Array.isArray(exam.patient_rdvs) &&
          exam.patient_rdvs?.length > 0
        ) {
          for (let i = 0; i < exam.patient_rdvs?.length; i++) {
            const rdv = exam.patient_rdvs[i];
            const rdv_date = rdv.date !== null ? moment(rdv.date) : null;
            if (
              rdv.date !== null &&
              rdv.status === "Pris" &&
              rdv_date?.isAfter(createdDate)
            ) {
              examRdvTaken = true;
              break;
            }
          }
        }
      });
      return examRdvTaken;
    }
  );

  return [
    { step: "Inscription", count: allPatients?.length },
    { step: "Confirmation du compte", count: confirmedPatients?.length },
    { step: "Questionnaire débuté", count: startedOnboardingPatients?.length },
    {
      step: "Questionnaire terminé",
      count: finishedOnboardingPatients?.length,
    },
    {
      step: "RDV pris avant inscription",
      count: updatedPlanningPatientsWithOldDate?.length,
    },
    { step: "RDV médecin généraliste", count: rdvTakenPatients?.length },
    {
      step: "RDV pris après inscription",
      count: updatedPlanningPatients?.length,
    },
  ];
};

/**
 * Find the smallest age within all patients
 * @param allPatients - Array of patient data
 * @returns The smallest age found
 */
export const getMinAge = (allPatients: any[]): number => {
  const validAges = extractAttribute(allPatients, "age");
  if (validAges?.length === 0) return 0;

  return Math.min(...validAges);
};

/**
 * Find the biggest age within all patients
 * @param allPatients - Array of patient data
 * @returns The biggest age found
 */
export const getMaxAge = (allPatients: any[]): number => {
  const validAges = extractAttribute(allPatients, "age");
  if (validAges?.length === 0) return 0;

  return Math.max(...validAges);
};

/**
 * Calculate the number of each gender if it exists
 * @param allPatients - Array of patient data
 * @returns Array of gender data with counts
 */
export const getGenderData = (allPatients: any[]) => {
  let genderCounts: Record<string, number> = {};

  const manLabel = i18next.t("common:responses.gender.man") || "Homme";
  const womanLabel = i18next.t("common:responses.gender.woman") || "Femme";
  const otherLabel = i18next.t("common:responses.gender.other") || "Autre";

  for (const patient of allPatients) {
    const gender = patient.patient_report?.gender;
    if (gender) {
      switch (gender) {
        case "Homme":
          genderCounts[manLabel] = (genderCounts[manLabel] || 0) + 1;
          break;
        case "Un homme":
          genderCounts[manLabel] = (genderCounts[manLabel] || 0) + 1;
          break;
        case "A man":
          genderCounts[manLabel] = (genderCounts[manLabel] || 0) + 1;
          break;
        case "Femme":
          genderCounts[womanLabel] = (genderCounts[womanLabel] || 0) + 1;
          break;
        case "Une femme":
          genderCounts[womanLabel] = (genderCounts[womanLabel] || 0) + 1;
          break;
        case "A woman":
          genderCounts[womanLabel] = (genderCounts[womanLabel] || 0) + 1;
          break;
        case "Autre":
          genderCounts[otherLabel] = (genderCounts[otherLabel] || 0) + 1;
          break;
        case "Autre (personne transgenre, non binaire)":
          genderCounts[otherLabel] = (genderCounts[otherLabel] || 0) + 1;
          break;
        case "Other":
          genderCounts[otherLabel] = (genderCounts[otherLabel] || 0) + 1;
          break;
        case " ":
          genderCounts[otherLabel] = (genderCounts[otherLabel] || 0) + 1;
          break;
        default:
          genderCounts[gender] = (genderCounts[gender] || 0) + 1;
          break;
      }
    }
  }

  return Object.entries(genderCounts).map(([gender, amount]) => ({
    gender,
    amount,
  }));
};

/**
 * Reports the number of appointments taken with a doctor, the patients who plan to do it, refuse to do it, or at least downloaded the pdf
 * @param allPatients - Array of patient data
 * @returns Array of states data with counts
 */
export const getAppointmentWithDocData = (allPatients: any[]) => {
  let stateCounts: Record<string, number> = {};

  const appointmentTakenLabel = "A pris un rendez-vous avec un médecin";
  const PlanToTakeAppointmentLabel =
    "Prévoit de prendre rendez-vous avec un médecin";
  const RefuseTakeAppointmentLabel =
    "Refuse de prendre rendez-vous avec un médecin";
  const DownloadedPdfLabel = "A téléchargé sa synthèse PDF";

  for (const patient of allPatients) {
    const patient_report = patient.patient_report;

    if (patient_report?.appointmentWithDoc === true) {
      stateCounts[appointmentTakenLabel] =
        (stateCounts[appointmentTakenLabel] || 0) + 1;
    } else if (patient_report?.rdvInfos?.refuseAppointment === true) {
      stateCounts[RefuseTakeAppointmentLabel] =
        (stateCounts[RefuseTakeAppointmentLabel] || 0) + 1;
    } else if (patient_report?.rdvInfos?.planToTakeAppointement === true) {
      stateCounts[PlanToTakeAppointmentLabel] =
        (stateCounts[PlanToTakeAppointmentLabel] || 0) + 1;
      // } else if (patient_report?.rdvInfos?.hasDownloadedPdf === true) {
      //     stateCounts[DownloadedPdfLabel] = (stateCounts[DownloadedPdfLabel] || 0) + 1;
    } else {
      stateCounts["N'a rien déclaré"] =
        (stateCounts["N'a rien déclaré"] || 0) + 1;
    }
  }

  return Object.entries(stateCounts).map(([state, amount]) => ({
    state,
    amount,
  }));
};

/**
 * Calculate the median of the time passed on the survey
 * @param allPatients - Array of patient data
 * @returns Time passed in minutes (median)
 */
export const getAverageSurveyTime = (allPatients: any[]): number => {
  // Median because some times are not representatives (ex: 3 hours afk)
  const validTimes: any[] = [];
  allPatients?.forEach((patient) => {
    const time = patient.patient_report?.totalDurationSession;
    if (time !== undefined && time != null) {
      const [hours, minutes, seconds] = time.split(":").map(Number);
      // Calculate the total of minutes spent
      const totalMinutes = hours * 60 + minutes + Math.round(seconds / 60);

      if (!isNaN(totalMinutes)) {
        validTimes.push(totalMinutes);
      }
    }
  });

  if (validTimes?.length === 0) {
    return 0; // If no valid times, return 0
  }

  // Sort valid times in ascending order
  validTimes.sort((a, b) => a - b); // Can be maybe long if many users ?

  const middleIndex = Math.floor(validTimes?.length / 2);

  if (validTimes?.length % 2 === 1) {
    // Odd number of elements: returns the central element
    return Math.floor(validTimes[middleIndex]); // Round down
  } else {
    // Even number of elements: returns the average of the two central elements
    return Math.floor(
      (validTimes[middleIndex - 1] + validTimes[middleIndex]) / 2
    ); // Round down
  }
};

export const getAllConnectedUsers = (allUsers: any[]) => {
  let connected = 0;
  allUsers?.forEach((user) => {
    // Patients
    if (user?.user_id?.isOnline === true) {
      connected++;
      // console.log(user?.user_id?.email);
    }
    // Doctors
    else if (user?.users_id?.isOnline === true) {
      // console.log(user?.users_id?.email);
      connected++;
    }
  });
  return connected;
};

/**
 * Returns the number of exams suggested and the number of appointments taken accordingly
 * @param {array} allPatients - An array containing patients' data
 * @returns {object} - An object containing the number of exams suggested (nbExams) and the number of appointments taken (nbRdv)
 */
export const getExamRdvRate = (allPatients: any[]) => {
  let nbExams = 0;
  let nbRdv = 0;
  allPatients?.forEach((patient) => {
    // const reportDate = patient?.patient_report?.createdAt; // TODO : vérifier uniquement les rdv pris apres utilisation de Lianeli -> compliqué à mettre en place et pas forcément utile ?
    nbExams += patient?.patient_exams?.length;
    patient?.patient_exams?.forEach((exam: any) => {
      if (
        exam?.patient_rdvs &&
        Array.isArray(exam?.patient_rdvs) &&
        exam?.patient_rdvs?.length > 0
      ) {
        for (let i = 0; i < exam?.patient_rdvs?.length; i++) {
          const rdv = exam?.patient_rdvs[i];
          if (rdv?.date !== null && rdv?.status === "Pris") {
            nbRdv++;
            break;
          }
        }
      }
    });
  });
  return {
    nbExams: nbExams,
    nbRdv: nbRdv,
  };
};

/**
 * Returns the total number of appointments taken
 * @param {array} allPatients - An array containing patients' data
 * @returns {number} - the number of appointments taken
 */

export const getExamRdvTotal = (allPatients: any[]) => {
  let nbRdv = 0;
  allPatients?.forEach((patient) => {
    if (patient?.patient_rdvs?.length > 0) {
      for (let i = 0; i < patient.patient_rdvs?.length; i++) {
        const rdv = patient.patient_rdvs[i];
        if (rdv.date !== null && rdv.status === "Pris") {
          nbRdv++;
        }
      }
    }
  });
  return nbRdv;
};

interface DailyCountData {
  Name: string;
  Date: string;
  Count: number;
}

/**
 * Associate the number of registrations or users per day
 * @param users - Array of user data
 * @param type - Type of user ('patient' or 'doctor')
 * @param countType - Type of count ('registrations' or 'users')
 * @returns {array} - Number of registrations or users for each day
 */
export const getDailyCounts = (
  users: any[] | undefined,
  type: string,
  countType: "registrations" | "users"
): DailyCountData[] => {
  if (!users) {
    return [];
  }
  const dateCountMap: { [date: string]: number } = {};

  // Extract dates and sort them
  users.forEach((user) => {
    let wasCreatedAt;
    if (type === "patient") {
      wasCreatedAt = user.patient_report?.createdAt;
    } else if (type === "doctor") {
      wasCreatedAt = user?.createdAt;
    }
    if (wasCreatedAt) {
      const wasCreatedAtDate = wasCreatedAt.split("T")[0];
      dateCountMap[wasCreatedAtDate] =
        (dateCountMap[wasCreatedAtDate] || 0) + 1;
    }
  });

  // Get sorted dates
  const sortedDates = Object.keys(dateCountMap).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  let cumulativeUsers = 0;
  const finalData: DailyCountData[] = sortedDates.map((date) => {
    if (countType === "users") {
      cumulativeUsers += dateCountMap[date];
      return {
        Name: type,
        Date: date,
        Count: cumulativeUsers,
      };
    } else {
      return {
        Name: type,
        Date: date,
        Count: dateCountMap[date] || 0,
      };
    }
  });

  return finalData;
};

/**
 * Associate the number of connections per day
 * @param users - Array of user data
 * @param type - Type of user ('patient' or 'doctor')
 * @returns {array} - Number of connections for each day
 */
export const getDailyConnections = (
  users: any[] | undefined,
  type: string
): DailyCountData[] => {
  if (!users) {
    return [];
  }
  const dateCountMap: { [date: string]: number } = {};

  // Extract dates and sort them
  users.forEach((user) => {
    let connections = [];
    if (type === "patient") {
      connections = user?.user_id?.log_connexions;
    } else if (type === "doctor") {
      connections = user?.users_id?.log_connexions;
    }
    if (connections) {
      connections.forEach((connection: any) => {
        const connectionDate = connection?.startDate?.split("T")[0];
        dateCountMap[connectionDate] = (dateCountMap[connectionDate] || 0) + 1;
      });
    }
  });

  // Get sorted dates
  const sortedDates = Object.keys(dateCountMap).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );

  const finalData: DailyCountData[] = sortedDates.map((date) => {
    return {
      Name: type,
      Date: date,
      Count: dateCountMap[date],
    };
  });

  return finalData;
};

/**
 * Function to calculate the average number of sessions by patients
 * @returns {number} - Average number of sessions by patients
 */
export const getAverageSessions = (allPatients: any[]): number => {
  const validSessions: number[] = [];
  allPatients?.forEach((patient) => {
    const nbSession = patient.patient_report?.nbSession;

    if (!isNaN(nbSession)) {
      validSessions.push(Number(nbSession));
    }
  });

  if (validSessions?.length === 0) {
    return 0; // If no valid times, return 0
  }

  // calculate average (sum / quantity)
  const sessionsAverage =
    validSessions?.reduce((acc, num) => acc + num, 0) / validSessions?.length;

  return Math.round(sessionsAverage);

  // OLD (median)
  // Sort valid times in ascending order
  // validSessions.sort((a, b) => a - b); // Long if many users -> optimization possible

  // const middleIndex = Math.floor(validSessions?.length / 2);

  // if (validSessions?.length % 2 === 1) {
  //     // Odd number of elements: returns the central element
  //     return Math.floor(validSessions[middleIndex]); // Round down
  // } else {//
  //     // Even number of elements: returns the average of the two central elements
  //     return Math.floor((validSessions[middleIndex - 1] + validSessions[middleIndex]) / 2); // Round down
  // }
};

/**
 * Function returning the smallest number of questions
 * @returns {number} - The smallest number of questions
 */
export const getMinQuestions = (allPatients: any[]): number => {
  let minQuestion = 10000; // Big value to start with
  allPatients?.forEach((patient) => {
    if (patient.patient_report?.surveyDone) {
      // To take into account only people who have completed the survey
      const nbQuestions =
        parseInt(patient.patient_report?.nbActiveQuestion) +
        parseInt(patient.patient_report?.nbQuestionSkip) +
        parseInt(patient.patient_report?.nbQuestionIgnore);
      if (!isNaN(nbQuestions)) {
        if (nbQuestions < minQuestion) {
          minQuestion = nbQuestions;
        }
      }
    }
  });
  return minQuestion;
};

/**
 * Function returning the biggest number of questions
 * @returns {number} - The biggest number of questions
 */
export const getMaxQuestions = (allPatients: any[]): number => {
  let maxQuestion = 0; // Small value to start with
  allPatients?.forEach((patient) => {
    if (patient.patient_report?.surveyDone) {
      // To take into account only people who have completed the survey
      const nbQuestions =
        parseInt(patient.patient_report?.nbActiveQuestion) +
        parseInt(patient.patient_report?.nbQuestionSkip) +
        parseInt(patient.patient_report?.nbQuestionIgnore);
      if (!isNaN(nbQuestions)) {
        if (nbQuestions > maxQuestion) {
          maxQuestion = nbQuestions;
        }
      }
    }
  });
  return maxQuestion;
};

/**
 * Function that returns the distribution of appointment types
 * @returns {Array} - The distribution of appointment types
 */
export const getRdvTypes = (allPatients: any[]) => {
  let typeCounts: Record<string, number> = {
    Visio: 0,
    "Sur site": 0,
    Annulé: 0,
  };

  allPatients?.forEach((patient) => {
    const attributes = patient?.patient_report;

    if (attributes) {
      const appointmentWithDoc = attributes.appointmentWithDoc;
      const rdvInfos = attributes.rdvInfos;

      if (appointmentWithDoc) {
        if (rdvInfos?.refuseAppointment) {
          typeCounts["Annulé"] += 1;
        } else if (rdvInfos?.rdvType === "Visio") {
          typeCounts["Visio"] += 1;
        } else if (rdvInfos?.rdvType !== null) {
          typeCounts["Sur site"] += 1;
        }
      }
    }
  });

  return Object.entries(typeCounts).map(([type, value]) => ({ type, value }));
};

/**
 * Function that returns the distribution of appointment taken between Lianeli doctors and other doctors
 * @param allPatients - Array of patient data
 * @returns {Array} - Array of users who booked an appointment with a Lianeli doctor or an other doctor
 */
export const getRdvDoctorType = (allPatients: any[]) => {
  // let lianeliDoctor = 0;
  // let otherDoctor = 0;
  let typeCounts: Record<string, number> = {
    "Docteur Lianeli": 0,
    "Docteur extérieur": 0,
  };
  allPatients?.forEach((patient) => {
    const surveyDone = patient?.patient_report?.surveyDone;
    const appointment =
      patient?.patient_report?.rdvInfos?.selectedTime?.startTime;
    const doctor = patient?.patient_report?.rdvInfos?.doctor;

    if (surveyDone) {
      if (appointment && doctor) {
        // If the user has an appointment with a Lianeli doctor
        typeCounts["Docteur Lianeli"]++;
        // lianeliDoctor++;
      } else if (appointment && !doctor) {
        typeCounts["Docteur extérieur"]++;
        // otherDoctor++;
      }
    }
  });
  const res = Object.entries(typeCounts).map(([type, value]) => ({
    type,
    value,
  }));
  return res;
};

/**
 * Function to obtain statistics on specifics questions
 * @param type - Type of questions wanted
 * @param amount - Amount of results wanted
 * @param allPatients - Array of patient data
 * @returns {Array} - Array of questions statistics
 */
export const getQuestionsStats = (
  type: string,
  amount: number,
  allPatients: any[]
) => {
  interface TransformData {
    name: string;
    uid: string;
    value: number;
  }

  const data = getIgnoredOrPassed(allPatients, type);
  const transformedData: TransformData[] = [];

  // Browse all categoryFrequency categories
  Object.entries(data).forEach(([categoryName, categoryData]) => {
    // Browse all reviews in this category
    Object.entries(categoryData.exams).forEach(([examName, examData]) => {
      transformedData.push({
        name: examName,
        uid: examData.uid,
        value: examData.value,
      });
    });
  });
  transformedData.sort((a, b) => b.value - a.value);
  const topTransformedData = transformedData.slice(0, amount);
  return topTransformedData;
};

/**
 * Function to get number of questions ignored or passed by categories
 * @param type - Type of questions wanted
 * @param allPatients - Array of patient data
 * @returns {Array} - Array of questions statistics
 */
export const getIgnoredOrPassed = (
  allPatients: any[],
  type: string
): CategoryFrequency => {
  const categoryFrequency: CategoryFrequency = {};
  let path = "";

  allPatients?.forEach((patient) => {
    if (type === "ignored") {
      path = patient?.patient_report?.ignoreQuestions?.questions;
    }
    if (type === "passed") {
      path = patient?.patient_report?.skipQuestions?.questions;
    }
    const questions = path;

    if (questions && Array.isArray(questions)) {
      questions.forEach((item: any) => {
        const questionName = item?.question?.title;
        const questionUid = item?.question?.uid;
        const categoryName = item?.category?.title;

        if (questionName && questionUid && categoryName) {
          // Initialize category if it doesn't exist
          if (!categoryFrequency[categoryName]) {
            categoryFrequency[categoryName] = {
              uid: "", // no category uid specified in patient-report JSON
              value: 0,
              exams: {},
            };
          }
          // Initialize exam if it doesn't exist
          if (!categoryFrequency[categoryName].exams[questionName]) {
            categoryFrequency[categoryName].exams[questionName] = {
              uid: questionUid,
              value: 0,
            };
          }
          // Increment counters
          categoryFrequency[categoryName].value += 1;
          categoryFrequency[categoryName].exams[questionName].value += 1;
        }
      });
    }
  });
  return categoryFrequency;
};

/**
 * Function to get a patient's report by ID
 * @param {string} patientId - The ID of the patient
 * @returns {Object|null} - The patient's report data or null if there's an error
 */
export const getPatientReportDatas = async (patientId: string) => {
  try {
    const response = await GetPatientReport(patientId);
    const { data } = response;
    return data; // Returns patient report data
  } catch (error) {
    console.error(
      `Error fetching patient report for patient ID ${patientId}:`,
      error
    );
    return null; // On error, returns null or a default value
  }
};

interface CategoryFrequency {
  [categoryName: string]: {
    uid: string;
    value: number;
    exams: {
      [examName: string]: {
        uid: string;
        value: number;
      };
    };
  };
}

/**
 * Function for counting exams and categories types by patient
 * @param allPatients - Array of patient data
 * @returns {Array} - Array of exams statistics
 */
export const countExamTypes = (allPatients: any[]): any => {
  const categoryFrequency: CategoryFrequency = {};

  allPatients?.forEach((patient) => {
    const exams = patient?.patient_exams;

    if (exams && Array.isArray(exams)) {
      exams.forEach((exam: any) => {
        const examName = exam.screening?.name;
        const examUid = exam.screening?.uid;
        const categoryName = exam.screening?.category_id?.name;
        const categoryUid = exam.screening?.category_id?.uid;

        if (examName && examUid && categoryName && categoryUid) {
          // Check if exam is to do 1 time only and was done
          if (
            exam?.frequency !== "Une fois" ||
            exam?.frequency !== "Once" ||
            exam?.patient_rdvs?.length === 0
          ) {
            // Initialize category if it doesn't exist
            if (!categoryFrequency[categoryName]) {
              categoryFrequency[categoryName] = {
                uid: categoryUid,
                value: 0,
                exams: {},
              };
            }
            // Initialize exam if it doesn't exist
            if (!categoryFrequency[categoryName].exams[examName]) {
              categoryFrequency[categoryName].exams[examName] = {
                uid: examUid,
                value: 0,
              };
            }
            // Increment counters
            categoryFrequency[categoryName].value += 1;
            categoryFrequency[categoryName].exams[examName].value += 1;
          }
        }
      });
    }
  });

  return categoryFrequency;
};

interface ToDoExams {
  examName: string;
  examUid: string;
  categoryName: string;
  categoryUid: string;
  scheduledDates: {
    patientUid: string;
    frequency: string;
    date: string;
  }[];
}

/**
 * Function for getting the list of exams to do for patients in a given period
 * @param allPatients - Array of patient data
 * @returns {Array} - Array of exams statistics
 */
export const getScreeningsToDo = (
  allPatients: any[],
  startDate: string,
  endDate: string
): ToDoExams[] => {
  const toDo: ToDoExams[] = [];

  allPatients?.forEach((patient) => {
    // Get patient uid and exams
    const exams = patient?.patient_exams;
    const patientUid = patient?.uid;

    if (exams && Array.isArray(exams)) {
      exams.forEach((exam: any) => {
        // Get exam infos and initiate the next rdvs list
        const frequency = exam.frequency;
        const examName = exam.screening?.name;
        const examUid = exam.screening?.uid;
        const categoryName = exam.screening?.category_id?.name;
        const categoryUid = exam.screening?.category_id?.uid;
        const examYear = exam.year;
        let examDatesList: {
          patientUid: string;
          frequency: string;
          date: string;
        }[];

        // Check if exam is valid and in the right period
        // Also exclude rdvs that should be done once and were done before the end of the period
        if (
          examName &&
          examUid &&
          categoryName &&
          categoryUid &&
          moment(`${examYear}-01-01`) <= moment(endDate) &&
          !(
            convertFrequency(frequency) === 0 &&
            exam?.patient_rdvs?.length > 0 &&
            moment(exam?.patient_rdvs[0]?.date) <= moment(endDate)
          )
        ) {
          // Get sorted done rdvs
          const sortedRdvs = exam?.patient_rdvs
            ? exam?.patient_rdvs.sort(
                (a: any, b: any) =>
                  moment(a.date || 0).valueOf() - moment(b.date || 0).valueOf()
              )
            : [];
          const filteredRdvs = sortedRdvs.filter((rdv: any) => rdv?.date);

          // Get rdvs that were already done in the given period, and/or the first next rdv to do in the period
          let periodRdvs = [];
          if (filteredRdvs) {
            for (const rdv of filteredRdvs) {
              if (
                moment(rdv?.date) >= moment(startDate) &&
                moment(rdv?.date) <= moment(endDate) &&
                !rdv?.neverDone &&
                rdv?.status === "Pris"
              ) {
                periodRdvs.push(rdv);
              }
            }
            const firstNextRdvDate = moment(
              filteredRdvs[filteredRdvs.length - 1]?.date
            ).add(convertFrequency(frequency), "months");
            if (firstNextRdvDate <= moment(endDate)) {
              periodRdvs.push(firstNextRdvDate);
            }
          }

          // Add already done rdvs in the list and get the last one
          let lastRdvDate;
          if (periodRdvs.length > 0) {
            examDatesList = periodRdvs.map((rdv) => {
              return { patientUid, frequency, date: rdv?.date };
            });
            lastRdvDate = periodRdvs[periodRdvs.length - 1]?.date;
          } else {
            // If no rdv was done/planned in the period, the first one is on the first day of the period (or the first day of the exam year)
            examDatesList = [
              {
                patientUid,
                frequency,
                date:
                  moment(startDate) >= moment(`${examYear}-01-01`)
                    ? startDate
                    : `${examYear}-01-01`,
              },
            ];
            lastRdvDate =
              moment(startDate) >= moment(`${examYear}-01-01`)
                ? startDate
                : `${examYear}-01-01`;
          }

          // With the last rdv date, figure out the next rdvs until endDate
          const monthFrequency = convertFrequency(frequency);
          if (monthFrequency > 0) {
            let nextDate = moment(lastRdvDate)
              .add(monthFrequency, "months")
              .format("YYYY-MM-DD");

            while (moment(nextDate) <= moment(endDate)) {
              examDatesList?.push({ patientUid, frequency, date: nextDate });
              nextDate = moment(nextDate)
                .add(monthFrequency, "months")
                .format("YYYY-MM-DD");
            }
          }

          // Return found rdvs to do in the global list
          const correspondingEntry = toDo?.find(
            (todo) => todo?.examName === examName
          );
          if (correspondingEntry) {
            examDatesList.length > 0 &&
              toDo
                .find((todo) => todo?.examName === examName)
                ?.scheduledDates?.push(...examDatesList);
          } else {
            toDo.push({
              examName,
              examUid,
              categoryName,
              categoryUid,
              scheduledDates: examDatesList,
            });
          }
        }
      });
    }
  });

  return toDo;
};

export const convertToCategoryFrequency = (toDoExams: ToDoExams[]) => {
  const categoryFrequency: any = {};

  toDoExams.forEach(
    ({ examName, examUid, categoryName, categoryUid, scheduledDates }) => {
      if (!categoryFrequency[categoryName]) {
        categoryFrequency[categoryName] = {
          uid: categoryUid,
          value: 0,
          exams: {},
        };
      }

      if (!categoryFrequency[categoryName].exams[examName]) {
        categoryFrequency[categoryName].exams[examName] = {
          uid: examUid,
          value: 0,
        };
      }

      const scheduledCount = scheduledDates.length;

      // Met à jour les valeurs
      categoryFrequency[categoryName].value += scheduledCount;
      categoryFrequency[categoryName].exams[examName].value += scheduledCount;
    }
  );

  return categoryFrequency;
};

/**
 * Function for counting exams done by patients in a given period
 * @param allPatients - Array of patient data
 * @returns {Array} - Array of exams statistics
 */
export const getNbScreeningsDone = (
  allPatients: any[],
  startDate: string,
  endDate: string
): number => {
  let done = 0;

  allPatients?.forEach((patient) => {
    // Get patient exams
    const exams = patient?.patient_exams;

    if (exams && Array.isArray(exams)) {
      exams.forEach((exam: any) => {
        const examName = exam.screening?.name;
        const examUid = exam.screening?.uid;
        const categoryName = exam.screening?.category_id?.name;
        const categoryUid = exam.screening?.category_id?.uid;
        const frequency = exam.frequency;

        // Check if the exam is valid and not punctual
        if (
          examName &&
          examUid &&
          categoryName &&
          categoryUid &&
          convertFrequency(frequency) !== 0
        ) {
          // Get rdvs that were done in the given period
          let periodRdvs = [];
          if (exam?.patient_rdvs) {
            for (const rdv of exam?.patient_rdvs) {
              if (
                moment(rdv?.date) >= moment(startDate) &&
                moment(rdv?.date) <= moment(endDate) &&
                !rdv?.neverDone &&
                rdv?.status === "Pris"
              ) {
                periodRdvs.push(rdv);
              }
            }
          }
          done += periodRdvs.length;
        }
      });
    }
  });

  return done;
};

/**
 * Function returning the name of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Name of the organization
 */
export const getOrgaName = (orga: any): string => {
  return orga?.name;
};

/**
 * Function returning the address of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Address of the organization
 */
export const getOrgaAddress = (orga: any): string => {
  return orga?.address;
};

/**
 * Function returning the type of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Type of the organization
 */
export const getOrgaType = (orga: any) => {
  return orga?.organization_type?.name;
};

/**
 * Function returning the created date of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Created date of the organization
 */
export const getOrgaDate = (orga: any): string => {
  const fullDate = orga?.createdAt;
  const dateObject = new Date(fullDate);
  const shortDate = dateObject.toLocaleDateString("en-GB"); // 'en-GB' for 'YYYY-MM-DD' format
  return shortDate;
};

/**
 * Function returning the uid of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Uid of the organization
 */
export const getOrgaUID = (orga: any): string => {
  return orga?.uid;
};

/**
 * Function returning the staff limit of the organization
 * @param orga - Array of organization datas
 * @returns {number} - Staff limit of the organization
 */
export const getOrgaStaff = (orga: any): number => {
  return orga?.organization_subscriptions?.staffNumber;
};

/**
 * Function returning the subscription state of the organization
 * @param orga - Array of organization datas
 * @returns {boolean} - The subscription state of the organization
 */
export const getOrgaSubValid = (orga: any): boolean => {
  return orga?.organization_subscriptions?.isValid;
};

/**
 * Function returning the number of patients of the organization
 * @param orga - Array of organization datas
 * @returns {number} - The number of patients of the organization
 */
export const getOrgaPatientsNumber = (orga: any): number => {
  return orga?.patients?.length || 0;
};

/**
 * Function returning the number of doctors of the organization
 * @param orga - Array of organization datas
 * @returns {number} - The number of doctors of the organization
 */
export const getOrgaDoctorsNumber = (orga: any): number => {
  return orga?.doctors?.length || 0;
};

/**
 * Function returning the subscription starting date of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Subscription starting date of the organization
 */
export const getOrgaStartSub = (orga: any): string => {
  return orga?.organization_subscriptions?.startDate;
};

/**
 * Function returning the subscription ending date of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Subscription ending date of the organization
 */
export const getOrgaEndSub = (orga: any): string => {
  return orga?.organization_subscriptions?.endDate;
};

/**
 * Function returning the subscription uid of the organization
 * @param orga - Array of organization datas
 * @returns {string} - Subscription uid of the organization
 */
export const getOrgaSubUid = (orga: any): string => {
  return orga?.organization_subscriptions?.uid;
};

/**
 * Function to fetch one or all organizations
 * @param uid - Uid of an organization
 */
export const fetchOrgas = async (uid: string): Promise<any[] | undefined> => {
  try {
    if (uid) {
      const response = await GetOrgData(uid);
      return response.data;
    } else {
      const response = await GetOrganization(uid);
      return response.data;
    }
  } catch (error: any) {
    console.error(error?.response?.data?.error?.message || error.message);
  }
};

/**
 * Function to fetch one organizations with period filtered patients
 * @param uid - Uid of an organization
 * @param startDate - begin date for patients creation date
 * @param endDate - end date for patients creation date
 */

export const fetchOrgaByPeriod = async (
  uid: string,
  startDate: string,
  endDate: string
): Promise<any[] | undefined> => {
  try {
    const response = await GetOrgDataByPeriod(uid, startDate, endDate);
    return response.data;
  } catch (error: any) {
    console.error(error?.response?.data?.error?.message || error.message);
  }
};

interface Subscriptions {
  id: number;
  attributes: {
    organization: any;
    startDate: string;
    endDate: string;
    sharedLink: string;
    signupCode: string;
    isValid: boolean;
    staffNumber: number;
    createdAt: string;
    updatedAt: string;
    publishedAt: string;
    uid: string;
  };
}

/**
 * Function to fetch all subscriptions of the organization
 */
export const fetchSubs = async (): Promise<Subscriptions[] | undefined> => {
  try {
    const response = await GetAllSubscriptions(); // Recovers all organizations
    const subscriptions = response.data?.data;
    if (subscriptions && Array.isArray(subscriptions)) {
      // Check that data is a valid array before using find()
      return subscriptions;
    }
  } catch (error: any) {
    console.error(error?.response?.data?.error?.message || error.message);
  }
};

/**
 * Function to retrieve a patient's last connection
 * @param patient - Patient datas
 * @returns {string} - Date of last connexion
 */
export const getLastConnexion = (patient: any): string => {
  if (
    patient?.user_id?.log_connexions &&
    patient?.user_id?.log_connexions?.length > 0
  ) {
    // Sort connections by end date in descending order
    patient.user_id.log_connexions.sort(
      (a: any, b: any) =>
        new Date(b.endDate).getTime() - new Date(a.endDate).getTime()
    );

    // Recover last connection
    const lastConnection = patient.user_id.log_connexions[0].endDate;

    return moment(lastConnection).fromNow();
  } else {
    return ""; // Return null if no connection found
  }
};

/**
 * Function to retrieve a patient's number of connexions
 * @param patient - Patient datas
 * @returns {number} - Date of last connexion
 */
export const getNbConnexions = (patient: any): number => {
  if (
    patient?.user_id?.log_connexions &&
    patient?.user_id?.log_connexions?.length > 0
  ) {
    return patient?.user_id?.log_connexions?.length;
  } else {
    return 0; // Return 0 if no connection found
  }
};

interface UserRequests {
  id: number;
  subject: string;
  description: string;
  answered: boolean;
  status: string;
  createdAt: string;
  endDate: string;
  user: {
    id: number;
    email: string;
  };
  files: any[];
}

/**
 * Function to fetch all UserRequests
 * @returns {Array} - Array of userRequests
 */
export const fetchUserRequests = async (): Promise<
  UserRequests[] | undefined
> => {
  try {
    const response = await GetRequestData(); // Recovers all UserRequests
    const requests = response.data;
    if (requests && Array.isArray(requests)) {
      // Check that data is a valid array before using find()
      return requests;
    }
  } catch (error: any) {
    console.error(error?.response?.data?.error?.message || error.message);
  }
};

/**
 * Function to get the subject of the request
 * @param userRequests - Array of all userRequests
 * @returns {Array} - The subject of the request
 */
export const getSubjectData = (userRequests: UserRequests[]) => {
  let subjectsCounts: Record<string, number> = {};

  userRequests.forEach((request) => {
    const subject = request?.subject;
    if (subject) {
      subjectsCounts[subject] = (subjectsCounts[subject] || 0) + 1;
    }
  });

  return Object.entries(subjectsCounts).map(([subject, amount]) => ({
    subject,
    amount,
  }));
};

/**
 * Function to get the answered rate of all requests
 * @param userRequests - Array of all userRequests
 * @returns {Array} - The answered rate of all requests
 */
export const getAnsweredRate = (userRequests: any[]) => {
  let nbResolved = 0;
  userRequests.forEach((request) => {
    if (request?.status === "Resolved") {
      nbResolved++;
    }
  });
  return nbResolved > 0 ? nbResolved / userRequests?.length : 0;
};

/**
 * Check if RDV is enabled
 * @param date
 * @returns
 */
export const disableCard = (date: any) => {
  let now = moment();
  let range = 6;
  const currentMonth = moment()?.month();
  let dateRange = now?.add(range, "month");
  const dateCompare = moment(date);

  if (currentMonth < 5) {
    if (dateCompare?.year() <= moment().year()) {
      return false;
    }
    return true;
  } else {
    if (dateCompare.isBefore(dateRange)) {
      return false;
    }
    return true;
  }
};

/**
 * Returns the same string with a capital letter on the first letter
 * @param str
 * @returns
 */
export const capitalizeFirstLetter = (str: string) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * prepare url swith application and screening type
 * @param type
 * @param application
 */
export const prepareUrlForSearch = (type: string, application: string) => {
  let url = "";
  switch (type) {
    case i18next.t("common:screeningTypes.biological_checkup"):
      // case "Analyse":
      if (application === "Doctolib")
        url = `https://www.doctolib.fr/doctors/laboratoire/`;
      if (application === "Maiia") url = `https://www.maiia.com`;
      if (application === "KelDoc")
        url = `https://www.keldoc.com/laboratoire-d-analyse/lyon`;
      break;
    case i18next.t("common:screeningTypes.imaging_examination"):
      if (application === "Doctolib")
        url = `https://www.doctolib.fr/doctors/imagerie`;
      if (application === "Maiia") url = `https://www.maiia.com`;
      if (application === "KelDoc") url = `https://www.keldoc.com/`;
      break;
    case i18next.t("common:screeningTypes.medical_act"):
    case "Médecin traitant":
      if (application === "Doctolib")
        url = `https://www.doctolib.fr/medecin-generaliste/`;
      if (application === "Maiia")
        url = `https://www.maiia.com/medecin-generaliste/`;
      if (application === "KelDoc") url = `https://www.keldoc.com`;
      break;
    default:
      if (application === "Doctolib") url = `https://www.doctolib.fr/`;
      if (application === "Maiia") url = `https://www.maiia.com/`;
      if (application === "KelDoc") url = `https://www.keldoc.com/`;
      break;
  }

  return url;
};

/**
 * get the correct language to display a doctor speciality
 * @param type
 * @param application
 */
export const translateDoctorSpeciality = (speciality: string) => {
  let translatedSpeciality = "";
  switch (speciality) {
    case "Médecin généraliste":
      translatedSpeciality = i18next.t("patient:general_practitioner");
      break;
    case "Infirmier(e)":
      translatedSpeciality = i18next.t("patient:nurse");
      break;
    case "Infirmier(e) IPA":
      translatedSpeciality = i18next.t("patient:general_practitioner");
      break;
    default:
      translatedSpeciality = i18next.t("patient:general_practitioner");
      break;
  }
  return translatedSpeciality;
};
